import { supabase } from "@/lib/supabase";
import { getOrderItemsForPicking, getOrderPickingMetrics } from "@/lib/pickingMetrics";

const PRINT_BASE_SIZES = ['P', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const compareSizeStrings = (a, b) => {
  const normalize = (s) => (s || '').toString().trim().toLowerCase();
  const na = normalize(a);
  const nb = normalize(b);

  const rank = {
    p: 1,
    s: 1,
    m: 2,
    l: 3,
    xl: 4,
    '2xl': 5,
    xxl: 5,
    '3xl': 6,
    xxxl: 6,
    '4xl': 7,
    '5xl': 8,
  };

  const ra = rank[na];
  const rb = rank[nb];
  if (ra !== undefined && rb !== undefined) return ra - rb;

  return na.localeCompare(nb, 'es', { numeric: true, sensitivity: 'base' });
};

const getCompactOrderItems = (order) => {
  const allItems = getOrderItemsForPicking(order);

  return [...allItems]
    .sort((a, b) => {
      const codeCompare = (a.product?.code || "").localeCompare(b.product?.code || "", "es", { numeric: true, sensitivity: "base" });
      if (codeCompare !== 0) return codeCompare;
      const colorCompare = (a.color || "").localeCompare(b.color || "", "es", { sensitivity: "base" });
      if (colorCompare !== 0) return colorCompare;
      return (a.size || "").localeCompare(b.size || "", "es", { numeric: true, sensitivity: "base" });
    });
};

const formatPrintAddress = (parts) => {
  const nonEmptyParts = parts.filter(Boolean);
  return nonEmptyParts.length > 0 ? nonEmptyParts.join(', ') : 'No especificada';
};

const normalizePrintSize = (size) => {
  if (!size) return 'N/A';
  const normalized = String(size).toUpperCase().trim();
  const aliases = {
    XS: 'P',
    S: 'P',
    P: 'P',
    M: 'M',
    L: 'L',
    XL: 'XL',
    XXL: '2XL',
    '2XL': '2XL',
    XXXL: '3XL',
    '3XL': '3XL',
    XXXXL: '4XL',
    '4XL': '4XL',
    XXXXXL: '5XL',
    '5XL': '5XL',
  };
  return aliases[normalized] || normalized;
};

const getSafeHexColor = (hex) => (
  /^#[0-9a-fA-F]{3,8}$/.test(hex || '') ? hex : '#d1d5db'
);

const renderPrintColorDot = (hex) => {
  const safeHex = escapeHtml(getSafeHexColor(hex));
  return `
    <svg class="color-dot" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <circle cx="6" cy="6" r="5.3" fill="${safeHex}" stroke="#111827" stroke-width="1" />
    </svg>
  `;
};

export const fetchPreparedPrintOrder = async (orderId) => {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      id,
      number,
      date,
      status,
      preparation_status,
      total,
      fecha_entrega,
      notes,
      forma_pago,
      confirmacion_90dias,
      shipping_address_id,
      discount_percentage,
      payment_transfer,
      customer:customers(
        id,
        name,
        email,
        phone,
        titular,
        street,
        city,
        province,
        postalCode
      ),
      items:order_items(
        id,
        quantity,
        pending_quantity,
        delivered_quantity,
        preparation_quantity,
        preparation_status,
        price,
        product_id,
        size,
        color,
        product:products(
          id,
          code,
          description,
          images,
          main_image_index,
          inventory(
            id,
            quantity,
            size,
            color
          )
        )
      ),
      shipping_address:shipping_addresses(
        id,
        name,
        street,
        city
      )
    `)
    .eq('id', orderId)
    .single();

  if (error) throw error;
  return data;
};

export const fetchCompanyInfoForPrint = async () => {
  const { data, error } = await supabase
    .from('app_settings')
    .select('nombre_empresa, calle_empresa, poblacion_empresa, provincia_empresa, cif_empresa, color_mappings')
    .single();

  if (error) throw error;
  return data || {};
};

const getPrintColorData = (colorId, companyInfo) => {
  const colorMappings = companyInfo?.color_mappings || {};
  const colorData = colorMappings[colorId];
  const hex = typeof colorData === 'string' ? colorData : colorData?.hex;
  const description = typeof colorData === 'string' ? colorId : colorData?.description;

  return {
    hex: getSafeHexColor(hex),
    label: description || colorId || 'Sin color',
  };
};

const getPreparedPrintStatus = ({ pending, prepared, delivered, missing }) => {
  if (pending <= 0 && delivered > 0) {
    return { text: 'Servido', className: 'served' };
  }
  if (pending <= 0) {
    return { text: 'Sin pendiente', className: 'served' };
  }
  if (missing <= 0) {
    return { text: 'Preparado', className: 'prepared' };
  }
  if (prepared > 0) {
    return { text: 'Parcial', className: 'partial' };
  }
  return { text: 'No preparado', className: 'missing' };
};

const buildPreparedPrintGroups = (items) => {
  const grouped = new Map();

  getCompactOrderItems({ all_order_items: items }).forEach((item) => {
    const productCode = item.product?.code || 'N/A';
    const productDescription = item.product?.description || '';
    const productId = item.product_id || item.product?.id || productCode;
    const color = item.color || 'N/A';
    const key = `${productId}-${color}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        product: item.product || {},
        productCode,
        productDescription,
        color,
        image: item.product?.images?.[item.product?.main_image_index || 0],
        sizes: {},
        total: 0,
        pending: 0,
        prepared: 0,
        delivered: 0,
        missing: 0,
      });
    }

    const group = grouped.get(key);
    const sizeKey = normalizePrintSize(item.size);
    if (!group.sizes[sizeKey]) {
      group.sizes[sizeKey] = {
        ordered: 0,
        pending: 0,
        prepared: 0,
        delivered: 0,
      };
    }

    const ordered = Number(item.quantity) || 0;
    const pending = Number(item.pending_quantity) || 0;
    const prepared = Number(item.preparation_quantity) || 0;
    const delivered = Number(item.delivered_quantity) || 0;

    group.sizes[sizeKey].ordered += ordered;
    group.sizes[sizeKey].pending += pending;
    group.sizes[sizeKey].prepared += prepared;
    group.sizes[sizeKey].delivered += delivered;
    group.total += ordered;
    group.pending += pending;
    group.prepared += prepared;
    group.delivered += delivered;
    group.missing += Math.max(0, pending - prepared);
  });

  return [...grouped.values()].sort((a, b) => {
    const byCode = a.productCode.localeCompare(b.productCode, 'es', { numeric: true, sensitivity: 'base' });
    if (byCode !== 0) return byCode;
    return a.color.localeCompare(b.color, 'es', { numeric: true, sensitivity: 'base' });
  });
};

const getPreparedPrintSizes = (groups) => {
  const sizes = new Set();
  groups.forEach((group) => {
    Object.keys(group.sizes).forEach((size) => sizes.add(size));
  });

  const standardSizes = PRINT_BASE_SIZES.filter((size) => sizes.has(size));
  const customSizes = [...sizes]
    .filter((size) => !PRINT_BASE_SIZES.includes(size))
    .sort(compareSizeStrings);

  return [...standardSizes, ...customSizes];
};

export const buildPreparedOrderPrintHtml = (order, companyInfo) => {
  const items = Array.isArray(order.items) ? order.items : [];
  const groups = buildPreparedPrintGroups(items);
  const sizes = getPreparedPrintSizes(groups);
  const metrics = getOrderPickingMetrics({ all_order_items: items });
  const missingTotal = groups.reduce((sum, group) => sum + group.missing, 0);
  const shippingAddress = order.shipping_address
    ? `${order.shipping_address.street || ''}, ${order.shipping_address.city || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, '')
    : formatPrintAddress([
        order.customer?.street,
        order.customer?.city,
        order.customer?.province,
        order.customer?.postalCode,
      ]);
  const printedAt = new Date().toLocaleString('es-ES');
  const rowsHtml = groups.map((group) => {
    const colorData = getPrintColorData(group.color, companyInfo);
    const status = getPreparedPrintStatus(group);
    const sizeCells = sizes.map((size) => {
      const cell = group.sizes[size];
      if (!cell || (!cell.ordered && !cell.pending && !cell.prepared && !cell.delivered)) {
        return '<td class="size-cell empty">-</td>';
      }

      const missing = Math.max(0, cell.pending - cell.prepared);
      const cellStatus = getPreparedPrintStatus({
        pending: cell.pending,
        prepared: cell.prepared,
        delivered: cell.delivered,
        missing,
      });

      return `
        <td class="size-cell ${cellStatus.className}">
          <div class="ordered">${cell.ordered}</div>
          <div class="prepared-line">Prep. ${cell.prepared}</div>
          ${missing > 0 ? `<div class="missing-line">Falta ${missing}</div>` : ''}
          ${cell.delivered > 0 ? `<div class="served-line">Serv. ${cell.delivered}</div>` : ''}
        </td>
      `;
    }).join('');

    return `
      <tr>
        <td class="product">
          <div class="product-code">${escapeHtml(group.productCode)}</div>
          <div class="product-description">${escapeHtml(group.productDescription)}</div>
        </td>
        <td class="image-cell">
          ${group.image
            ? `<img src="${escapeHtml(group.image)}" alt="${escapeHtml(group.productDescription || group.productCode)}" class="product-image">`
            : '<div class="product-image placeholder">Sin imagen</div>'
          }
        </td>
        <td class="color-cell">
          ${renderPrintColorDot(colorData.hex)}
          <span>${escapeHtml(colorData.label)}</span>
        </td>
        ${sizeCells}
        <td class="number total">${group.total}</td>
        <td class="number prepared-total">${group.prepared}</td>
        <td class="number missing-total">${group.missing}</td>
        <td class="status ${status.className}">${status.text}</td>
      </tr>
    `;
  }).join('');

  return `<!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8">
        <title>Preparación pedido #${escapeHtml(order.number || 'N/A')}</title>
        <style>
          * { box-sizing: border-box; }
          html, body, table, tr, th, td, div, span, svg {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body {
            margin: 18px;
            color: #111827;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            gap: 18px;
            margin-bottom: 12px;
          }
          .company { width: 35%; }
          .customer { width: 60%; }
          .company-name, .customer-name {
            margin-bottom: 4px;
            font-size: 16px;
            font-weight: 700;
          }
          .title {
            margin: 0 0 8px;
            text-align: center;
            font-size: 18px;
            font-weight: 700;
          }
          .details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px 16px;
            margin-top: 8px;
            font-size: 8px;
          }
          .summary {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin: 8px 0 10px;
          }
          .summary-item {
            border: 1px solid #d1d5db;
            border-radius: 4px;
            padding: 4px 7px;
            background: #f9fafb;
            font-size: 9px;
          }
          .summary-item strong { font-size: 11px; }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }
          th, td {
            border: 1px solid #bfbfbf;
            padding: 4px;
            vertical-align: middle;
          }
          th {
            background: #f0f0f0;
            font-weight: 700;
            text-align: center;
          }
          .product { width: 22%; }
          .image-heading, .image-cell { width: 6%; text-align: center; }
          .color-heading, .color-cell { width: 10%; }
          .totals-heading { width: 5%; }
          .status-heading, .status { width: 8%; text-align: center; }
          .product-code { font-weight: 700; }
          .product-description {
            margin-top: 2px;
            color: #4b5563;
            font-size: 8px;
            line-height: 1.2;
          }
          .product-image {
            display: block;
            width: 30px;
            height: 30px;
            margin: 0 auto;
            object-fit: contain;
            border: 1px solid #d1d5db;
            background: #fff;
          }
          .product-image.placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7280;
            font-size: 7px;
          }
          .color-cell {
            font-size: 8px;
            text-align: left;
          }
          .color-dot {
            display: inline-block;
            width: 12px;
            height: 12px;
            margin-right: 4px;
            vertical-align: -2px;
          }
          .size-heading { min-width: 38px; }
          .size-cell {
            min-width: 38px;
            text-align: center;
            line-height: 1.2;
          }
          .size-cell.empty {
            color: #9ca3af;
            background: #f9fafb;
          }
          .ordered {
            font-size: 12px;
            font-weight: 700;
          }
          .prepared-line { color: #047857; }
          .missing-line {
            color: #b91c1c;
            font-weight: 700;
          }
          .served-line { color: #2563eb; }
          .number {
            text-align: center;
            font-weight: 700;
          }
          .prepared, .prepared-total {
            background: #dcfce7;
            color: #166534;
          }
          .partial {
            background: #fef3c7;
            color: #92400e;
          }
          .missing, .missing-total {
            background: #fee2e2;
            color: #991b1b;
          }
          .served {
            background: #dbeafe;
            color: #1d4ed8;
          }
          .legend {
            display: flex;
            gap: 10px;
            margin-top: 8px;
            color: #374151;
            font-size: 8px;
          }
          .notes {
            margin-top: 10px;
            padding: 8px;
            border: 1px solid #d1d5db;
            background: #f9fafb;
          }
          @media print {
            @page { size: A4 landscape; margin: 10mm; }
            body { margin: 0; }
            .no-print { display: none; }
            tr { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">
            <div class="company-name">${escapeHtml(companyInfo?.nombre_empresa || 'Empresa')}</div>
            <div>${escapeHtml(companyInfo?.calle_empresa || '')}</div>
            <div>${escapeHtml(formatPrintAddress([companyInfo?.poblacion_empresa, companyInfo?.provincia_empresa]))}</div>
            <div>CIF: ${escapeHtml(companyInfo?.cif_empresa || '')}</div>
          </div>
          <div class="customer">
            <h1 class="title">Preparación pedido #${escapeHtml(order.number || 'N/A')}</h1>
            <div class="customer-name">${escapeHtml(order.customer?.name || 'Cliente no especificado')}</div>
            ${order.customer?.titular ? `<div>Nombre comercial: ${escapeHtml(order.customer.titular)}</div>` : ''}
            <div class="details">
              <div>Fecha pedido: ${escapeHtml(new Date(order.date).toLocaleDateString('es-ES'))}</div>
              <div>Fecha entrega: ${escapeHtml(order.fecha_entrega ? new Date(order.fecha_entrega).toLocaleDateString('es-ES') : 'No especificada')}</div>
              <div>Dirección: ${escapeHtml(shippingAddress || 'No especificada')}</div>
              <div>Impreso: ${escapeHtml(printedAt)}</div>
              <div>Teléfono: ${escapeHtml(order.customer?.phone || 'No especificado')}</div>
              <div>Email: ${escapeHtml(order.customer?.email || 'No especificado')}</div>
            </div>
          </div>
        </div>

        <div class="summary">
          <div class="summary-item">Total prendas: <strong>${metrics.totalQuantity}</strong></div>
          <div class="summary-item">Preparadas: <strong>${metrics.preparedQuantity}</strong></div>
          <div class="summary-item">No preparadas: <strong>${missingTotal}</strong></div>
          <div class="summary-item">Servidas: <strong>${metrics.servedQuantity}</strong></div>
          <div class="summary-item">Estado: <strong>${escapeHtml(metrics.derivedStatusText)}</strong></div>
        </div>

        <table>
          <thead>
            <tr>
              <th class="product">Producto</th>
              <th class="image-heading">Imagen</th>
              <th class="color-heading">Color</th>
              ${sizes.map((size) => `<th class="size-heading">${escapeHtml(size)}</th>`).join('')}
              <th class="totals-heading">Total</th>
              <th class="totals-heading">Prep.</th>
              <th class="totals-heading">Falta</th>
              <th class="status-heading">Estado</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || `<tr><td colspan="${sizes.length + 7}">Sin líneas para imprimir</td></tr>`}
          </tbody>
        </table>

        <div class="legend">
          <span>Verde: preparado completo.</span>
          <span>Rojo: falta preparar.</span>
          <span>Amarillo: preparado parcial.</span>
          <span>Azul: ya servido.</span>
        </div>

        ${order.notes ? `<div class="notes"><strong>Observaciones:</strong> ${escapeHtml(order.notes)}</div>` : ''}

        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
          };
        </script>
      </body>
    </html>`;
};

export const printPreparedOrderDocument = async ({
  orderId,
  toast,
  onPrintingChange,
}) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast?.({
      title: "No se pudo abrir la impresión",
      description: "Revisa el bloqueador de ventanas emergentes e inténtalo de nuevo.",
      variant: "destructive",
    });
    return false;
  }

  printWindow.document.write('<!doctype html><html><head><title>Cargando impreso</title></head><body style="font-family: Arial, sans-serif; padding: 24px;">Preparando impreso...</body></html>');
  printWindow.document.close();
  onPrintingChange?.(true);

  try {
    const [preparedOrder, companyInfo] = await Promise.all([
      fetchPreparedPrintOrder(orderId),
      fetchCompanyInfoForPrint(),
    ]);
    printWindow.document.open();
    printWindow.document.write(buildPreparedOrderPrintHtml(preparedOrder, companyInfo));
    printWindow.document.close();
    toast?.({
      title: "Impreso preparado",
      description: `Se ha abierto el impreso del pedido #${preparedOrder.number}.`,
    });
    return true;
  } catch (error) {
    console.error('Error printing prepared order:', error);
    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html><body style="font-family: Arial, sans-serif; padding: 24px;">No se pudo generar el impreso: ${escapeHtml(error.message || 'Error desconocido')}</body></html>`);
    printWindow.document.close();
    toast?.({
      title: "Error",
      description: "No se pudo generar el impreso preparado",
      variant: "destructive",
    });
    return false;
  } finally {
    onPrintingChange?.(false);
  }
};
